// M08 SAP/Kasse reporting API hooks. Money fields are euro string-decimals
// (the Functions API converts BIGINT cents → strings at the boundary).
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { api } from '../../../../shared/api'
import { notify } from '../../../../store/notificationStore'

export interface SapVatRow { rate: number; netto: string; steuer: string; brutto: string }
export interface SapKioskTotals {
  netto: string
  brutto: string
  bar: string
  unbar: string
  bar_pct: number
  dayclosings: number
  receipts: number
  avg_per_receipt: string
  vat: SapVatRow[]
}
export interface SapMonthRow { month: string; brutto: string; bar: string; unbar: string; dayclosings: number; receipts: number }
export interface SapSchoolRow {
  school_id: number
  school_name: string | null
  firma: string | null
  netto: string
  brutto: string
  bar: string
  unbar: string
  dayclosings: number
  receipts: number
  /** Gruppiert mode only: set on a folded Küchen-Gruppe row (school_id = owner, school_name = kitchen label). */
  kitchenId?: number
  members?: { school_id: number; school_name: string | null }[]
}
export interface SapFirmaKpi {
  firma: string; netto: string; brutto: string; bar: string; unbar: string
  bar_pct: number; dayclosings: number; receipts: number; avg_per_receipt: string
}
export interface SapKioskSummary {
  period: { from: string; to: string }
  dimension: SapDimension
  entity: string
  grouped?: boolean   // echo of the Gruppiert|Einzeln request flag
  totals: SapKioskTotals
  byMonth: SapMonthRow[]
  bySchool: SapSchoolRow[]
  byFirma?: SapFirmaKpi[]   // Firma → Alle Firmen: per-company KPI rows
}
export interface SapArticle { art_nr: string | null; name: string | null; warengr: string | null; menge: number; netto: string; brutto: string; lines: number }
export interface SapArticlesResponse { period: { from: string; to: string }; dimension: SapDimension; entity: string; warengr: string | null; warengruppen: string[]; articles: SapArticle[] }
export interface SapRollupResult { refreshed: boolean; converted: number; pending: number; newSince: number; since: string | null; schools: number; partitions: number; agg: number; vat: number; closing: number; unmappedKassen: string[] }

function qs(params: Record<string, string | number | null | undefined>): string {
  return Object.entries(params)
    .filter(([, v]) => v != null && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
    .join('&')
}

// The single page filter (mirrors the explorer): KPIs, Schulen/Steuer (summary)
// and Artikel resolve from the same dimension × entity × granularity × period.
// grouped: Küchen-Gruppen cumulated (default) vs. every school on its own — same toggle as the Auswertung.
export interface SapDimQuery { dimension: SapDimension; entity: string; granularity: SapGranularity; period: string; grouped?: boolean }
const groupedQs = (g: boolean | undefined) => (g === false ? 0 : undefined)   // default (grouped) sends nothing

export function useSapKioskSummary(p: SapDimQuery, enabled = true) {
  return useQuery({
    queryKey: ['sap-kiosk-summary', p.dimension, p.entity, p.granularity, p.period, p.grouped !== false],
    queryFn: () => api.get<SapKioskSummary>(`/api/sap/kiosk/summary?${qs({ dimension: p.dimension, entity: p.entity, granularity: p.granularity, period: p.period, grouped: groupedQs(p.grouped) })}`),
    staleTime: 60_000,
    enabled,
  })
}

export function useSapKioskArticles(p: SapDimQuery & { limit?: number; warengr?: string | null }, enabled = true) {
  return useQuery({
    queryKey: ['sap-kiosk-articles', p.dimension, p.entity, p.granularity, p.period, p.limit ?? 50, p.warengr ?? null, p.grouped !== false],
    queryFn: () => api.get<SapArticlesResponse>(`/api/sap/kiosk/articles?${qs({ dimension: p.dimension, entity: p.entity, granularity: p.granularity, period: p.period, limit: p.limit, warengr: p.warengr, grouped: groupedQs(p.grouped) })}`),
    staleTime: 60_000,
    enabled,
  })
}

// Every "Aktualisieren" (SAP/Kasse header, Imports page) goes through here, so the
// refetch + feedback live in ONE place: invalidate every consumer of the SAP cache
// (this tab + the Auswertung/Deep-Dive Kiosk income) and say what happened.
export function useSapRollup() {
  const qc = useQueryClient()
  const { t } = useTranslation('kostenstellen')
  return useMutation({
    mutationFn: () => api.post<SapRollupResult>('/api/sap/rollup', {}),
    onSuccess: async (r) => {
      await Promise.all(['sap-kiosk-summary', 'sap-kiosk-articles', 'sap-kiosk-series'].map((k) => qc.invalidateQueries({ queryKey: [k] })))
      await qc.invalidateQueries({ queryKey: ['pca', 'income-by-school'] })
      // "N neue Abschlüsse seit HH:MM" — closings that entered the cache with THIS refresh.
      const sinceTxt = r.since ? new Date(r.since).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'
      const title = t('sapKasse.actions.syncDone', { count: r.newSince, since: sinceTxt, schools: r.schools })
      if (r.pending > 0) notify.warning(title, t('sapKasse.actions.syncPending', { count: r.pending }))
      else if (r.unmappedKassen?.length) notify.warning(title, t('sapKasse.actions.syncUnmapped', { kassen: r.unmappedKassen.join(', ') }))
      else notify.success(title)
    },
    onError: (e) => notify.error(t('sapKasse.actions.syncFailed'), e instanceof Error ? e.message : String(e)),
  })
}

// ── Dimensional explorer series ───────────────────────────────────────────────
export type SapDimension = 'firma' | 'school' | 'leitung'
export type SapGranularity = 'year' | 'month' | 'day' | 'schuljahr'
// In split mode points carry dynamic g<i>_bar / g<i>_unbar keys (one bar-group per member).
export interface SapSeriesPoint { label: string; value?: string; bar?: string; unbar?: string; compareValue?: string | null; [groupKey: string]: string | null | undefined }
export interface SapSeriesGroup { key: string; label: string; total: string }
/** A till's newest imported day-closing; behind = still missing sales (last closing on an earlier day than the others). */
export interface SapTillStand { kasse: string; closedAt: string; behind: boolean }
export interface SapSeriesResponse {
  dimension: SapDimension
  granularity: SapGranularity
  period: string
  entity: string
  grouped?: boolean   // echo of the Gruppiert|Einzeln request flag
  compare: boolean
  entities: { value: string; label: string }[]
  availableYears: number[]
  series: SapSeriesPoint[]
  total: string
  compareTotal?: string
  // 'firma' = Firma → Alle Firmen (one stacked bar per company);
  // 'entities' = multi-select compare (2–5 members side by side, Brutto).
  split?: 'firma' | 'entities' | null
  groups?: SapSeriesGroup[]                  // g0, g1 … → member labels + totals
  stand?: SapTillStand[]                     // how far each selected till's data reaches (line under the chart)
}
// ── Manual ZIP backfill (Gap Filler) ──────────────────────────────────────────
export interface SapBackfillFile { name: string; b64: string }
export interface SapBackfillVerdict {
  name: string | null; dayclosing_id: string | null; z_kasse_id: string | null
  school_id: string | null; revenue: string | null
  verdict: 'new' | 'duplicate' | 'revision' | 'error'; error: string | null
  // Revision only: on-file revenue this import would overwrite, the delta, and how
  // many versions are already stored for this dayclosing_id.
  existingRevenue?: string | null; revenueDelta?: string | null; existingCount?: number | null
  school_name?: string | null         // resolved school label (replaces the raw zip path)
  business_date?: string | null       // earliest receipt start (the business day)
  // Revision only: field-level diff (stored version vs incoming) + the exact receipts.
  diff?: { existing: SapClosingFigures | null; incoming: SapClosingFigures | null; receipts: SapReceiptDiff | null } | null
}
export interface SapClosingFigures {
  revenue: string | null; bar: string | null; unbar: string | null
  receipts: number | null; erstellung: string | null
}
export interface SapReceiptBon { id: string | null; amount: string | null; end: string | null }
export interface SapReceiptChange { id: string | null; from: string | null; to: string | null; end: string | null }
export interface SapReceiptDiff {
  addedCount: number; removedCount: number; changedCount: number
  added: SapReceiptBon[]; removed: SapReceiptBon[]; changed: SapReceiptChange[]
}
export interface SapBackfillPreview { files: SapBackfillVerdict[]; summary: Record<string, number> }
export interface SapBackfillCommitResult {
  committed: number; enqueued: number
  inserted: { dayclosing_id: string | null; verdict: string }[]
  skipped: { name: string | null; dayclosing_id: string | null; verdict: string; error: string | null }[]
  total: number; note: string
}
export function useSapBackfillPreview() {
  return useMutation({ mutationFn: (p: { files: SapBackfillFile[] }) => api.post<SapBackfillPreview>('/api/sap/backfill/preview', p) })
}
export function useSapBackfillCommit() {
  return useMutation({ mutationFn: (p: { files: SapBackfillFile[]; includeRevisions: boolean }) => api.post<SapBackfillCommitResult>('/api/sap/backfill/commit', p) })
}

export function useSapKioskSeries(p: {
  dimension: SapDimension; granularity: SapGranularity; period?: string; entity?: string; compare?: boolean; grouped?: boolean
}) {
  return useQuery({
    queryKey: ['sap-kiosk-series', p.dimension, p.granularity, p.period ?? '', p.entity ?? 'all', !!p.compare, p.grouped !== false],
    queryFn: () => api.get<SapSeriesResponse>(
      `/api/sap/kiosk/series?${qs({ dimension: p.dimension, granularity: p.granularity, period: p.period, entity: p.entity ?? 'all', compare: p.compare ? 1 : 0, grouped: groupedQs(p.grouped) })}`),
    staleTime: 60_000,
    // The entity picker's options + availableYears ride on this response — keep the
    // last one while a granularity/period switch refetches, or the picker goes blank.
    // But only while the option SET is the same (same dimension + Gruppiert/Einzeln):
    // stale options from another set could be picked mid-refetch and select a dead entity.
    placeholderData: (prev?: SapSeriesResponse) =>
      prev && prev.dimension === p.dimension && (prev.grouped ?? true) === (p.grouped !== false) ? prev : undefined,
  })
}
